import { z } from 'zod';
import { NoteTargetType } from '../note.js';
/**
 * Quick-capture entity-suggest wire shapes.
 *
 * The QuickCapture SlideOver (`apps/web/src/components/quick-capture/*`)
 * debounces the user's draft text into `POST /api/orgs/:o/quick-capture/
 * suggest`. The server lexical-prefilters the org's known entities
 * (bookmarks + org-pinned entities) and re-ranks the top candidates via
 * a Vertex Gemini call, returning the top-5 with a 1-line reason per row.
 *
 * `NoteTargetType` (`person`/`customer`/`topic`/`product`/`entity`) is
 * the canonical dossier alphabet for note attachments — re-using it here
 * means the suggestions slot directly into the note's `targets[]` array
 * when the user clicks "+ Add all N".
 *
 * Multi-tenant: every request is `orgMember+` scoped; the candidate
 * universe is filtered to that org's bookmarks / pinned entities only.
 *
 * Rate-limit: tight (30 calls/min/user) — this endpoint is called on
 * debounce as the user types.
 */
/**
 * Request body for `POST /api/orgs/:o/quick-capture/suggest`.
 *
 *   - `text` is the current draft body; we cap at 2000 chars so the
 *     prompt budget stays bounded even on pasted articles.
 *   - `contextEntity` is the dossier the user was looking at when they
 *     opened quick-capture (e.g. "thinking about Acme Foundry"). The
 *     server boosts candidates that share connections with this entity
 *     in the graph; if absent, ranking is purely text-driven.
 */
export const QuickCaptureSuggestRequest = z.object({
    text: z.string().min(3).max(2000),
    contextEntity: z
        .object({
        kind: NoteTargetType,
        key: z.string().min(1).max(200),
    })
        .optional(),
});
/**
 * One ranked entity in the suggestions array.
 *
 *   - `kind`/`key` slot directly into a `NoteTarget` on the client side.
 *   - `label` is the snapshot display name at suggest time (the dossier
 *     can rename later; `<EntityName>` re-resolves on render).
 *   - `confidence` is the model's self-assessment 0..1. Below 0.5 the
 *     UI should hide the suggestion or mark it warning.
 *   - `reason` is the 1-line "why this matches" (≤160 chars) the agent
 *     shows next to the entity. It's interpretation, not a citation —
 *     no INTERP tag is required because the row itself is clearly a
 *     suggestion the user can accept or skip.
 */
export const QuickCaptureSuggestEntity = z.object({
    kind: NoteTargetType,
    key: z.string().min(1).max(200),
    label: z.string().min(1).max(200),
    confidence: z.number().min(0).max(1),
    reason: z.string().max(160),
});
/**
 * Response from `POST /api/orgs/:o/quick-capture/suggest`. `tookMs` is
 * surfaced so the client can decide whether to throttle further (a
 * 4s synthesis means we should back off; a 200ms cache hit means we
 * can re-fire freely).
 */
export const QuickCaptureSuggestResponse = z.object({
    suggestions: z.array(QuickCaptureSuggestEntity).max(5),
    tookMs: z.number().int().min(0),
});
